<template>
  <div class="table-root-div">
    <!-- DEBUG INFO -->
    <div v-if="showDebugInfo">
      <div v-for="row in localRows" :key="row.tempID">
        {{ row }}
      </div>
    </div>

    {{ widthOverrides }}

    <!-- TABLE CONTROLS -->
    <div 
    v-if="!hideToolbar"
    class="table-toolbar">
      <template v-if="checkable">
        <v-btn
        flat 
        icon 
        :class="{'header-checkbox-left-margin': viewMode === 0}"
        @click="selectAllSwitch = !selectAllSwitch">
          <v-icon>{{ selectAllSwitch ? 'check_box' : 'check_box_outline_blank' }}</v-icon>
        </v-btn>

        <v-btn flat icon @click="deleteRows">
          <v-icon>delete</v-icon>
        </v-btn>
      </template>

      <v-btn
      v-if="expandable"
      :class="{'rotate-anim': rowIsExpanded}"
      icon
      @click="toggleAllRowsExpanded">
        <v-icon :class="{'blue-grey--text text--darken-2': rowIsExpanded}">
          arrow_drop_down
        </v-icon>
      </v-btn>

      <slot name="toolbar"></slot>

      <v-btn
      v-if="debugMode"
      flat icon @click="showDebugInfo = !showDebugInfo">
        <v-icon>info</v-icon>
      </v-btn>

      <v-btn
      v-if="viewMode === 0"
      flat 
      icon 
      @click="rescaleTable">
        <v-icon>zoom_out_map</v-icon>
      </v-btn>

      <v-menu offset-y left open-on-hover>
        <v-btn flat icon slot="activator">
          <v-icon>visibility</v-icon>
        </v-btn>

        <v-list>
          
          <v-list-tile  @click="viewMode = 0" :class="{ 'green--text text--accent-3' : viewMode == 0}">
            <v-icon :class="{ 'green--text text--accent-3' : viewMode == 0}">view_list</v-icon>
            <v-list-tile-title>
              Table
            </v-list-tile-title>
          </v-list-tile>

          <v-list-tile  @click="viewMode = 1" :class="{ 'green--text text--accent-3' : viewMode == 1}">
            <v-icon :class="{ 'green--text text--accent-3' : viewMode == 1}">phone_android</v-icon>
            <v-list-tile-title>
              List
            </v-list-tile-title>
          </v-list-tile>

        </v-list>
      </v-menu>
    </div>
    <!-- END TABLE CONTROLS -->

    <div :class="[{'table': viewMode === 0}, {'grid-table': viewMode === 1}]">

      <!-- TABLE BODY-->
      <v-layout
      ref="tbody"
      wrap 
      column
      class="table-container ma-0">

        <!-- TABLE HEADERS -->
        <div
        v-if="viewMode === 0"
        draggable="false"
        class="row-container header-row-container">
          <div
          class="header-row unselectable">

            <div
            class="row-toolbar">
              <div
              v-if="draggable"
              style="width: 24px; flex-shrink: 0;">
              </div>

              <div
              v-if="checkable"
              style="width: 40px; flex-shrink: 0;">
              </div>
            </div>

            <div class="inner-row" :style="gridStyle">

              <template v-for="(header, index) in headers">
                <div 
                :key="index" 
                :class="['crx-cell header-cell', {'dragging-header-cell': drag.index === index}]" 
                :ref="'header' + index"
                @mousedown="startDrag(index, $event)"
                @mouseup="stopDrag(index, $event)">
                  <span class="span-single-line-ellipsed">
                    {{ header }}
                  </span>
                </div>

              </template>

            </div>
          </div>
        </div>

        <v-card v-if="rows.length < 1">
          <v-card-text>
            No data to display
          </v-card-text>
        </v-card>

        <!-- ROWS -->

        <draggable 
        v-model="localRows" 
        :options="{handle:'.drag-handle'}"
        class="draggable-component">

          <virtual-list 
          v-if="localRows.length > 0"
          :size="74" 
          :remain="8" 
          :bench="20"
          class="virtual-list">
        

            <div
            v-for="row in localRows"
            class="row-container"
            :key="row.tempID">

            <!-- this div contains the row so the expansion slot at 100% width can 
            expand to the table-row width -->
              <div :style="[{'width: 100%': viewMode === 1}]">

                <div
                @click.stop="onRowClicked(row)"
                :class="['table-row hoverable', {'last-visited': lastMobileRowClicked === row.tempID && viewMode === 1}]">

                  <div class="row-toolbar">

                    <v-checkbox
                    v-if="checkable"
                    v-model="selected"
                    :value="row.tempID"
                    @click.native.stop
                    hide-details
                    color="blue-grey darken-2"
                    :class="['shrink ma-0 ml-2 row-checkbox']"></v-checkbox>

                    <v-icon
                    v-if="draggable"
                    class="drag-handle">
                      drag_indicator
                    </v-icon>

                  </div>

                  <slot
                  v-if="viewMode === 0"
                  name="row" 
                  :row="row"
                  :widthOverrides="widthOverrides"
                  :viewMode="viewMode">
                  </slot>
                  <slot
                  v-else
                  name="compact-row" 
                  :row="row"
                  :viewMode="viewMode">
                  </slot>

                </div>

                <transition appear v-on:enter="expand" v-on:leave="collapse" v-bind:css="false">
                  <div
                  v-if="expanded[row.tempID]"
                  class="expansion-slot">
                    <slot name="expand" :row="row"></slot>
                  </div>
                </transition>

              </div>

            </div>

          </virtual-list>

        </draggable>

        <!-- END ROWS -->

      </v-layout>
      <!-- END TABLE BODY-->

    </div>

    <v-dialog v-model="dialog" content-class="green-dialog" scrollable persistent max-width="500px">
      
      <v-card-title class="dialog-title-bar pa-2 pl-4 pr-4">
        <v-layout align-center>
          <v-btn
          icon
          class="blue-grey--text text--darken-3"
          round @click.native="dialog = false">
            <v-icon>close</v-icon>
          </v-btn>
          <v-spacer></v-spacer>
          <span class="title font-weight-medium black--text">
            Edit
          </span>
          <v-spacer></v-spacer>
          <v-btn
          flat
          class="blue-grey--text text--darken-3"
          round @click.native="mobileUpdateRow()">
            <v-icon>check</v-icon>
          </v-btn>
        </v-layout>
      </v-card-title>
      
      <v-card class="grid-table border-radius-a-0">
        <v-card-text class="pt-0 green-scrollbar">
          <slot v-if="dialog" name="row" :row="dialogRow"></slot>
        </v-card-text>
      </v-card>

    </v-dialog>

  </div>
</template>

<script>
import Vue from 'vue'
import draggable from 'vuedraggable'
import Velocity from 'velocity-animate'

export default {
  props: {
    rows: {
      default: function () {
        return []
      }
    },
    headers: {
      default: function () {
        return []
      }
    },
    checkable: { // checkable, deletable, draggable
      type: Boolean,
      default: true
    },
    draggable: {
      default: false
    },
    expandable: {
      type: Boolean,
      default: false
    },
    hideToolbar: {
      type: Boolean,
      default: false
    }
  },
  components: {
    draggable
  },
  data () {
    return {
      drag: {
        dragging: false,
        startX: 0,
        startWidth: 0,
        index: null
      },
      viewMode: 1, // 0 - table, 1 - mobile
      selected: [],
      expanded: {},
      tempIDcounter: 0, // counter
      selectAllSwitch: false,
      showDebugInfo: false,
      gridStyle: '',
      widthOverrides: [],
      dialog: false,
      dialogRow: {},
      dialogRowToOverwrite: null,
      lastMobileRowClicked: null
    }
  },
  computed: {
    localRows: {
      get () {
        return this.rows
      },
      set (nv) {
        this.$emit('update:rows', nv)
      }
    },
    debugMode () {
      return this.$store.state.settings.debugMode
    },
    totalWidth () {
      var dragMargin = this.draggable ? 24 : 0
      var expandMargin = this.expandable ? 24 : 0
      var checkable = this.checkable ? 40 : 0
      // var borderWidth = 2
      var cellSum = this.widthOverrides.reduce((i, a) => {
        a += i
        return a
      }, 0)
      return cellSum + checkable + dragMargin + expandMargin
    },
    rowIsExpanded () {
      for (var state of Object.values(this.expanded)) {
        if (state) {
          return true
        }
      }
      return false
    }
  },
  watch: {
    selectAllSwitch (nv) {
      this.selected = []
      if (nv) {
        for (var row of this.localRows) {
          this.selected.push(row.tempID)
        }
      }
    },
    viewMode (nv) {
      if (nv === 0) {
        this.rescaleTable()
      } else {
        // if not changed to table mode
        this.gridStyle = ''
      }
    }
  },
  methods: {
    rescaleTable () {
      // do it in next tick to accomodate dialogs brief incorrect height
      this.$nextTick(() => {
        var minWidth = 120
        var maxWidth = 200
        // subtract 32 = checkbox size
        // 9 ->? magic number for padding and border?
        // 24 draggable icon
        var dragMargin = this.draggable ? 24 : 0
        var expandMargin = this.expandable ? 24 : 0
        var checkable = this.checkable ? 40 : 0
        var borderWidth = 2
        var cellSize = (this.$refs.tbody.clientWidth - checkable - dragMargin - expandMargin - borderWidth) / this.headers.length
        if (cellSize < minWidth) {
          cellSize = minWidth
        }
        if (cellSize > maxWidth) {
          cellSize = maxWidth
        }
        var r = (cellSize + 'px ').repeat(this.headers.length)
        this.gridStyle = {
          display: 'grid',
          gridTemplateColumns: r.trim()
        }
        this.widthOverrides = []
        for (var index in this.headers) {
          Vue.set(this.widthOverrides, index, cellSize)
        }
      })
    },
    onMouseMove (e) {
      if (this.drag.dragging) {
        var difference = this.drag.startWidth + (e.pageX - this.drag.startX)
        if (difference < 20) { // minimum cell width (should be positive)
          difference = 20
        }
        Vue.set(this.widthOverrides, this.drag.index, difference)
        this.generateStyle()
      }
    },
    startDrag (index, e) {
      // @click="$refs['header' + index][0].style.width = '200px'"
      this.drag.dragging = true
      this.drag.index = index
      this.drag.startX = e.pageX
      var cell = e.currentTarget
      // var cell = e.target ? e.target : e.srcElement
      console.log(cell)
      this.drag.startWidth = cell.clientWidth
      console.log(cell.clientWidth)
    },
    stopDrag (index, e) {
      this.drag.dragging = false
      this.drag.index = null
    },
    generateStyle () {
      var r = ''
      for (var index in this.headers) {
        if (this.widthOverrides[index]) {
          r += this.widthOverrides[index] + 'px '
        } else {
          r += '200px '
          // r += '1fr '
        }
      }
      this.gridStyle = {
        display: 'grid',
        gridTemplateColumns: r
      }
    },
    getNewID () {
      this.tempIDcounter++
      return this.tempIDcounter
    },
    deleteRows () {
      for (var i = this.localRows.length - 1; i >= 0; --i) {
        if (this.selected.includes(this.localRows[i].tempID)) {
          this.localRows.splice(i, 1)
        }
      }
      this.selected = []
      this.selectAllSwitch = false
    },
    updateIDsAfterSave (responseRows) {
      // if a newly created row has been saved, it now has an id
      for (var row of responseRows) {
        this.localRows.find(item => {
          return item.tempID === row.tempID
        }).id = row.id
      }
    },
    toggleAllRowsExpanded () {
      if (this.rowIsExpanded) {
        this.expanded = {}
      } else {
        for (var row of this.rows) {
          Vue.set(this.expanded, row.tempID, true)
        }
      }
    },
    onRowClicked (row) {
      this.lastMobileRowClicked = row.tempID
      if (this.viewMode === 1) {
        this.dialogRow = JSON.parse(JSON.stringify(row))
        this.dialogRowToOverwrite = row.tempID
        this.dialog = true
      }
      /* if (this.expanded[row.tempID]) {
        Vue.set(this.expanded, row.tempID, false)
      } else {
        Vue.set(this.expanded, row.tempID, true)
      } */
    },
    mobileUpdateRow () {
      this.dialog = false
      Object.assign(this.rows[this.rows.findIndex(el => el.tempID === this.dialogRowToOverwrite)], this.dialogRow)
    },
    // ANIMATION
    expand: function (el, done) {
      Velocity(el, 'slideDown', { duration: 500 })
      Velocity(el, { opacity: 1 }, { complete: done })
    },
    collapse: function (el, done) {
      Velocity(el, 'slideUp', { duration: 500 })
      Velocity(el, { opacity: '0%' }, { complete: done })
    }
  },
  created () {
    if (this.$vuetify.breakpoint.xs) {
      this.viewMode = 1
    } else {
      this.viewMode = 0 // should be 0
    }
    this.rescaleTable()
    window.addEventListener('mouseup', this.stopDrag)
    window.addEventListener('mousemove', this.onMouseMove)
  }
}
</script>

<style>
.virtual-list {
  max-height: 50vh;
}
.header-checkbox-left-margin {
  margin-left: 2px;
}
.span-single-line-ellipsed {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.table-toolbar {
  display: flex;
  background: rgb(243, 246, 248);
}
.row-toolbar {
  display: flex;
}
.draggable-component {
}
.drag-handle {
  cursor: move;
}
.row-checkbox .v-icon {
  color: #CFD8DC;
}

.row-container {
  display: flex;
  flex-direction: column;
  /* width: 100%; */
}

.row-container:last-child {
  border-bottom: none;
}

.table-row {
  display: flex;
  align-items: center;
  /* width: 100%; */
}

.table .crx-cell-label {
  display: none;
}

.inner-row {
  display: flex;
}

.grid-table .inner-row {
  flex-direction: column;
}

.header-cell:last-child {
  border: none;
}

.header-row {
  display: flex;
  align-items: center;
  background: linear-gradient(#CFD8DC, #B0BEC5);
  background: linear-gradient(#A7FFEB, #64FFDA);
  background: linear-gradient(#9a55fe, #783aff);
  background: linear-gradient(#ECEFF1, #CFD8DC);
  background: linear-gradient(#37474F, #263238);
  background: transparent;
  z-index: 1;/* to make the box shadow visible*/
  color: #555;
  text-transform: capitalize;
  box-shadow: 2px 2px 5px 0px rgb(243, 246, 248);
  font-size: 15px;
  font-weight: bold;
  /* /* background: #363b4b; */
}

.header-row-container {
  /* incase table is shorted than 100% width */
  /* background: #37474F; */
}

.expansion-slot {
    box-shadow: inset 0px 2px 5px rgba(0,0,0,0.2);
    padding-left: 10px;
    padding-right: 10px;
    padding-bottom: 10px;
}

/* ------------------ TABLE -------------- */

.table .inner-row {
  border-left: 1px solid rgba(0,0,0,0.05);
}

.table .draggable-component {
  /* flex-direction: column; */
}

.table .row-container {
  align-items: flex-start;
  border-bottom: 1px solid rgba(0,0,0,0.05);
}

.table .row-container:nth-child(even) .table-row{
  /* background: rgb(243, 246, 248); */
}

.table .table-container {
  overflow-x: auto;
  overflow-y: hidden;
}

.table .crx-cell {
  border-right: 1px solid rgba(0,0,0,0.05);
  min-height: 48px;
  max-height: 48px;
  overflow: hidden;
  padding-left: 8px;
  padding-right: 8px;
}

.table .header-cell {
  align-items: center;
  position: relative;
  cursor: w-resize;
}

/* these two need to be the same */
.table .header-cell:hover {
  color: #555;
  background: rgb(243, 246, 248);
  box-shadow: 0px 1px 3px 0px rgba(0,0,0,0.1), inset 0px 1px rgba(0,0,0,0.1);
  transition: box-shadow 0.1s ease-in-out, -webkit-box-shadow 0.3s ease-in-out;
}

.dragging-header-cell {
  color: #555;
  background: rgb(243, 246, 248);
  box-shadow: 0px 1px 3px 0px rgba(0,0,0,0.1), inset 0px 1px rgba(0,0,0,0.1);
  transition: box-shadow 0.1s ease-in-out, -webkit-box-shadow 0.3s ease-in-out;
}
/* these two need to be the same */

.table .crx-cell:first-child {
}

.table .crx-table-row-img {
  display: none;
}

.table-row.hoverable:hover {
  cursor: pointer;
  background: rgba(0,0,0,0.02);
}

.grid-table .table-row.hoverable:hover {
  /* border-radius: 4px; */
}

.table .table-row {
  align-items: center;
  flex-direction: row;
  background: white;
  border-top: none;
  min-height: 48px;
  box-shadow: 0px 1px 0px 0px rgb(243, 246, 248);
}

/* ------------------ GRID -------------- */
.grid-table {
  border: none;
}

.grid-table .crx-input::before {
  content: none;
}

.grid-table .crx-table-row-img {
  align-self: center;
}

.grid-table .row-container {
  border-bottom: thin solid rgba(0,0,0,0.14);
  align-items: normal;
}

.grid-table .row-container .last-visited {
  box-shadow: inset 0.5rem 0px #CFD8DC, inset -0.5rem 0px #CFD8DC;
  transition: box-shadow 0.3s ease-in-out;
}

.grid-table .crx-cell {
  flex-direction: column;
  justify-content: start;
  align-items: start;
  border-bottom: 1px solid rgb(243, 246, 248);
  padding: 0px 10px;
  margin-bottom: 6px;
}

.grid-table .row-checkbox {
  align-self: flex-end;
  color: red;
}

.grid-table .row-container:nth-child(even) {
  background: none;
}

.grid-table .table-row {
  flex: 1;
  justify-content: start;
  align-items: start;
  flex-direction: row;
  padding: 6px;
  box-shadow: none;
  align-items: center;
}

/* labels */
.grid-table .crx-cell-label {
  display: block;
}

/* ANIMATION */
.rotate-anim {
  transform: rotate( -180deg );            
  transition: transform 300ms ease;
}
</style>
